// DraftResult.js
// Shared result-screen components, used by BOTH the regular DraftComplete and
// KnockoutComplete screens — mirroring how iOS shares TeamRatingSection,
// CircularScoreDisplay, RosterPlayerCardCondensed, and getOrderedPlayerIndices
// between DraftCompleteView and KnockoutCompleteView.
//
// Extracted verbatim from DraftComplete.js (styles included) so the regular
// draft screen is visually unchanged; the only parameter added is `sportLabel`
// on TeamRatingSection so Knockout can show "KNOCKOUT MODE" in place of
// "{SPORT} DRAFTS".

import React, { useState } from "react";
import FitText from "./FitText";
import { bundledLogo, logoURL } from "../managers/teamLogoService";
import {
  getChemistryBreakdown,
  effectiveScaledPoints,
} from "../managers/teamRatingCalculator";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";

const SPORT_ICONS = {
  football: footballIcon,
  baseball: baseballIcon,
  basketball: basketballIcon,
  hockey: hockeyIcon,
  soccer: soccerIcon,
};

// Order players for the 2-column roster grid to match the app's layout.
// Ported from DraftCompleteViewModel.getOrderedPlayerIndices. The grid fills
// row-major (L0, R0, L1, R1…), so each pair below is one row: left then right.
export function getOrderedPlayerIndices(sportId, players = [], positions = []) {
  const total = players.length;
  const ordered = [];
  const interleave = (left, right) => {
    const rows = Math.max(left.length, right.length);
    for (let row = 0; row < rows; row++) {
      if (row < left.length && left[row] < total) ordered.push(left[row]);
      if (row < right.length && right[row] < total) ordered.push(right[row]);
    }
  };

  switch (sportId) {
    case "football": {
      // Offense 0–6 (left), defense 7–13 (right), interleaved by row.
      const offenseCount = Math.min(7, total);
      const defenseCount = Math.max(0, total - 7);
      const rows = Math.max(offenseCount, defenseCount);
      for (let row = 0; row < rows; row++) {
        if (row < offenseCount && row < total) ordered.push(row);
        if (row < defenseCount) {
          const defIdx = row + 7;
          if (defIdx < total) ordered.push(defIdx);
        }
      }
      break;
    }
    case "baseball":
      interleave([8, 0, 1, 2, 4, 3], [5, 6, 7, 9, 10, 11]);
      break;
    case "soccer":
      interleave([10, 8, 9, 7, 6, 5], [3, 4, 2, 1, 0]);
      break;
    case "basketball":
      interleave([0, 1, 2, 3, 4], [5, 6, 7, 8, 9]);
      break;
    case "hockey": {
      // Group by position label: left = first of each, right = second of each.
      const byPos = {};
      for (let i = 0; i < total; i++) {
        const pos = (positions[i] || "").toUpperCase();
        (byPos[pos] = byPos[pos] || []).push(i);
      }
      const order = ["LW", "C", "RW", "LD", "RD", "G"];
      const left = [], right = [];
      for (const pos of order) {
        const group = byPos[pos] || [];
        if (group[0] !== undefined) left.push(group[0]);
        if (group.length > 1) right.push(group[1]);
      }
      const rows = Math.max(left.length, right.length);
      for (let row = 0; row < rows; row++) {
        if (row < left.length) ordered.push(left[row]);
        if (row < right.length) ordered.push(right[row]);
      }
      break;
    }
    default:
      for (let i = 0; i < total; i++) ordered.push(i);
  }

  return ordered;
}

// ─── Circular Score Display ───────────────────────────────────────────────────
// Mirrors CircularScoreDisplay.swift: track ring + animated arc + rating number.
// Size/stroke/font are optional — defaults reproduce the original 116px wheel
// exactly, so DraftComplete is unchanged; KnockoutComplete passes a larger size.
export function CircularScore({
  teamRating,
  sport,
  hasAppeared,
  size = 116,
  strokeWidth = 10,
  trackStrokeWidth = 14,
  ratingFontSize,
  scoreLabelFontSize,
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = hasAppeared ? (teamRating / 999) * circumference : 0;

  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={trackStrokeWidth}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke={sport.hex}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          style={{ transition: "stroke-dashoffset 1.5s ease" }}
        />
      </svg>
      <div style={styles.circleCenter}>
        <span style={{ ...styles.circleRating, color: sport.hex,
          ...(ratingFontSize ? { fontSize: ratingFontSize } : null),
          transform: hasAppeared ? "scale(1)" : "scale(0.5)",
          opacity: hasAppeared ? 1 : 0,
          transition: "all 0.6s ease 0.25s",
        }}>
          {teamRating}
        </span>
        <span style={{ ...styles.circleScoreLabel,
          ...(scoreLabelFontSize ? { fontSize: scoreLabelFontSize } : null),
        }}>SCORE</span>
      </div>
    </div>
  );
}

// ─── Condensed Roster Card ────────────────────────────────────────────────────
// Mirrors RosterPlayerCardCondensed.swift
export function RosterCardCondensed({ player, position, sport, index, hasAppeared }) {
  const [logoError, setLogoError] = useState(false);

  const isSoccer = sport.id === "soccer";
  const bundled = bundledLogo(player.city, sport.id);
  const url = bundled ? null : logoURL(player.city, sport.id);
  const logoSrc = bundled || url;
  const showLogo = !!logoSrc && !logoError;

  const nameParts = (player.name || "").trim().split(/\s+/);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ");

  return (
    <div style={{
      ...styles.rosterCard,
      opacity: hasAppeared ? 1 : 0,
      transform: hasAppeared ? "translateX(0)" : "translateX(20px)",
      transition: `all 0.25s ease ${0.3 + index * 0.03}s`,
    }}>
      {showLogo && (
        <div
          className="rb-roster-logo"
          style={isSoccer ? styles.rosterLogoSoccer : styles.rosterLogo}
        >
          <img
            src={logoSrc}
            alt=""
            onError={() => setLogoError(true)}
            style={isSoccer ? styles.rosterLogoImgSoccer : styles.rosterLogoImg}
          />
        </div>
      )}
      <div style={styles.rosterPillStack}>
        <span style={{ ...styles.rosterPosPill, background: sport.hex }}>{position}</span>
        <span style={styles.rosterYearPill}>{player.year}</span>
      </div>
      <div style={styles.rosterNameBlock}>
        <FitText text={firstName} style={styles.rosterName} minScale={0.55} />
        {lastName && <FitText text={lastName} style={styles.rosterName} minScale={0.55} />}
      </div>
    </div>
  );
}

// ─── Team Rating Section ──────────────────────────────────────────────────────
// Score wheel + chemistry panel. Mirrors TeamRatingSection.swift. Computes
// chemistry from the players internally so callers only pass the roster + the
// already-resolved teamRating. `sportLabel` lets Knockout show "KNOCKOUT MODE".
export function TeamRatingSection({ sport, teamRating, players, hasAppeared, sportLabel }) {
  const chemistry = getChemistryBreakdown(players);
  const hasChemistry = chemistry.totalChemistryPoints > 0;
  const chemDisplayText = hasChemistry ? `+${chemistry.totalChemistryPoints}` : "+0";

  // Top 3 active bonuses by effective scaled points, highest → lowest.
  const sortedBonuses = [...chemistry.activeBonuses]
    .map(b => ({ bonus: b, pts: effectiveScaledPoints(b, chemistry.activeBonuses, players.length) }))
    .filter(x => x.pts > 0)
    .sort((a, b) => b.pts - a.pts)
    .map(x => x.bonus)
    .slice(0, 3);

  return (
    <div style={{
      ...styles.section,
      opacity: hasAppeared ? 1 : 0,
      transition: "opacity 0.4s ease 0.15s",
    }}>
      <div style={styles.heroRow}>
        <CircularScore teamRating={teamRating} sport={sport} hasAppeared={hasAppeared} />

        <div style={styles.chemPanel}>
          <div style={styles.sportLabelRow}>
            <span style={{
              display: "inline-block", width: 14, height: 14, flexShrink: 0,
              backgroundColor: sport.hex,
              WebkitMaskImage: `url(${SPORT_ICONS[sport.id]})`,
              maskImage: `url(${SPORT_ICONS[sport.id]})`,
              WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
              WebkitMaskSize: "contain", maskSize: "contain",
              WebkitMaskPosition: "center", maskPosition: "center",
            }} />
            <span style={styles.sportLabelText}>{sportLabel}</span>
          </div>

          <div style={{ ...styles.rule, background: sport.hex }} />

          {[0, 1, 2].map(i => {
            const bonus = sortedBonuses[i];
            if (bonus) {
              const cleanLabel = bonus.description
                .replace(/ Connection$/, "")
                .replace(/ Team Chemistry$/, "")
                .replace(/ Mixed Chemistry$/, "")
                .toUpperCase();
              const pts = effectiveScaledPoints(bonus, chemistry.activeBonuses, players.length);
              return (
                <div key={i} style={styles.bonusRow}>
                  <span style={styles.bonusLabel}>{cleanLabel}</span>
                  <span style={{ ...styles.bonusPts, color: sport.hex }}>+{pts}</span>
                </div>
              );
            }
            return (
              <div key={i} style={styles.bonusRow}>
                {i === 0 && !hasChemistry
                  ? <span style={styles.noChemLabel}>NO CHEMISTRY BONUS</span>
                  : <span style={{ opacity: 0 }}>_</span>
                }
              </div>
            );
          })}

          <div style={{ ...styles.rule, background: sport.hex }} />

          <div style={styles.chemTotalRow}>
            <span style={styles.chemTotalLabel}>CHEMISTRY</span>
            <span style={{ ...styles.chemTotalBadge, background: sport.hex }}>
              {chemDisplayText}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  section: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "10px",
  },
  heroRow: { display: "flex", alignItems: "center", gap: "20px" },
  chemPanel: { flex: 1, display: "flex", flexDirection: "column", gap: "5px", minWidth: 0 },
  sportLabelRow: { display: "flex", alignItems: "center", gap: "5px" },
  sportLabelText: {
    fontSize: "13px",
    fontWeight: "800",
    color: "#ffffff",
    letterSpacing: "1.2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rule: { height: "1px", opacity: 0.5, borderRadius: "1px" },
  bonusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "20px",
  },
  bonusLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.75)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  bonusPts: { fontSize: "12px", fontWeight: "700", flexShrink: 0, marginLeft: "6px" },
  noChemLabel: {
    fontSize: "12px",
    fontWeight: "600",
    fontStyle: "italic",
    color: "rgba(255,255,255,0.3)",
  },
  chemTotalRow: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  chemTotalLabel: { fontSize: "10px", fontWeight: "800", color: "#ffffff", letterSpacing: "1.2px" },
  chemTotalBadge: {
    fontSize: "11px",
    fontWeight: "800",
    color: "#000000",
    padding: "3px 7px",
    borderRadius: "5px",
  },
  rosterGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" },
  circleCenter: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  circleRating: { fontSize: "28px", fontWeight: "800", lineHeight: 1, display: "inline-block" },
  circleScoreLabel: {
    fontSize: "9px",
    fontWeight: "700",
    color: "rgba(255,255,255,0.4)",
    letterSpacing: "2px",
    marginTop: "1px",
  },
  rosterCard: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.09)",
    borderRadius: "10px",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minHeight: "44px",
    minWidth: 0,
  },
  rosterLogoSoccer: {
    width: "30px",
    height: "20px",
    flexShrink: 0,
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  rosterLogo: {
    width: "30px",
    height: "30px",
    flexShrink: 0,
    background: "#c2c2c2",
    borderRadius: "6px",
    border: "1px solid #8c8c8c",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    padding: "2px",
    boxSizing: "border-box",
  },
  rosterLogoImgSoccer: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  rosterLogoImg: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" },
  rosterPillStack: { display: "flex", flexDirection: "column", gap: "2px", flexShrink: 0 },
  rosterPosPill: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#000",
    padding: "1px 4px",
    borderRadius: "4px",
    textAlign: "center",
    minWidth: "30px",
  },
  rosterYearPill: {
    fontSize: "10px",
    fontWeight: "400",
    color: "rgba(255,255,255,0.7)",
    background: "rgba(255,255,255,0.08)",
    padding: "1px 4px",
    borderRadius: "4px",
    textAlign: "center",
    minWidth: "30px",
  },
  rosterNameBlock: { display: "flex", flexDirection: "column", gap: "1px", flex: 1, minWidth: 0 },
  rosterName: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#fff",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    letterSpacing: "0.2px",
  },
};

export { styles as draftResultStyles };
